"use client";
import React, { useState, useEffect } from "react";
import { useTransactionFlow } from "@/context/TransactionContext";
import { ACTIONS } from "@/app/components/payment/actions";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { ButtonSize, RegularButton } from "@/stories/button/button";
import Image from "next/image";
import { postOrderDetailsByISIN } from "@/app/api/service/stocks";
import { ThirdPartyData } from "@/app/interface/docvault";
import { RZPModalDismiss, RZPCallback, RazorpayPaymentDetails } from "@/app/api/service/razorpay";
import NEFTModal from "./neft";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { generateUUID } from "@/app/utils/helper";
import { UPI_MAX_AMT } from "@/app/utils/constants";
import useMobile from "@/app/hooks/use-mobile";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
// import { initBranch, trackEvent } from "@/app/utils/branchio";
import Info from "@/app/assets/icons/info";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Razorpay: any;
    }
}

interface OrderDetails {
    url?: string;
    name?: string;
    email?: string;
    phone?: string;
    key?: string;
    amount?: number;
    orderId?: string;
    currency?: string;
    units?: number;
    userAmount?: number;
    contact?: string;
}

interface PaymentStepProps {
    isAdvisoryFlow?: boolean;
    onComplete: () => void;
    thirdPartyData: ThirdPartyData | undefined;
    userId?: string | undefined;
}

export enum PaymentMethod {
    UPI = "upi",
    NET_BANKING = "netbanking",
    NEFT = "neft",
}

const PaymentStep: React.FC<PaymentStepProps> = ({ thirdPartyData, isAdvisoryFlow }) => {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = new URLSearchParams(useSearchParams());
    const { dispatch, state } = useTransactionFlow();
    const { isMobile } = useMobile();
    const { cleverTapTrackEvent } = useCleverTapEvents();

    const [paymentMode, setpaymentMode] = useState<PaymentMethod>(
        state.allowedTxnType.upi
            ? PaymentMethod.UPI
            : state.allowedTxnType.netbanking
              ? PaymentMethod.NET_BANKING
              : PaymentMethod.NEFT,
    );
    const [showNEFTUI, setshowNEFTUI] = useState<boolean>(false);
    const [isDialogOpen, setisDialogOpen] = useState<boolean>(true);
    const [bankName, setBankName] = useState("");

    const paymentMethodConfigs = [
        {
            key: "upi",
            method: PaymentMethod.UPI,
            label: "UPI",
            text: "",
            condition:
                state.allowedTxnType.upi && (state.postDigioTxnData?.amount ?? 0) <= UPI_MAX_AMT,
        },
        {
            key: "netbanking",
            method: PaymentMethod.NET_BANKING,
            label: "NetBanking",
            text: "Check your bank’s net banking limits before choosing this payment option for transactions above ₹2 lakhs.",
            condition: state.allowedTxnType.netbanking,
        },
        {
            key: "neft",
            method: PaymentMethod.NEFT,
            label: "NEFT/RTGS",
            text: "",
            condition: state.allowedTxnType.neft,
        },
    ];

    const appendUUIDToURL = () => {
        const newUUID = generateUUID();
        searchParams.set("ref", newUUID);
        router.replace(`${pathname}?${searchParams.toString()}`, { scroll: false });
    };

    const getValidBankName = (name: string): string => {
        let bankName = "";
        const validateAgainst = ["NA", "null", "undefined", "--", ""];

        try {
            if (
                name.trim().length > 1 &&
                !validateAgainst.some((element) => element === name)
            ) {
                bankName = name.trim();
            }
            return bankName;
        } catch (ex) {
            console.error("Bank Name error: ", ex);
            return bankName;
        }
    };

    useEffect(() => {
        setAnalyticsEvent("", `US_Payment_Initiated`);
        cleverTapTrackEvent(`US_Payment_Initiated`, {});
        // initBranch()
        //     .then(() => {
        //         trackEvent(`US_Payment_Initiated`);
        //     })
        //     .catch((error) => console.error("Error initializing Branch:", error));

        setBankName(getValidBankName(thirdPartyData?.bankName ?? ""));

        // First check if script already exists to avoid duplicates
        if (document.querySelector('script[src*="checkout.razorpay.com"]')) {
            return;
        }

        const loadRazorpay = () => {
            return new Promise((resolve, reject) => {
                const script = document.createElement("script");
                // Ensure the URL is properly encoded
                const baseUrl = "https://checkout.razorpay.com/v1/checkout.js";
                const cleanUrl = encodeURI(baseUrl);
                script.src = cleanUrl;

                // Add integrity and crossorigin for better security
                script.crossOrigin = "anonymous";

                let isScriptLoaded = false;

                script.onload = () => {
                    isScriptLoaded = true;
                    // Check if Razorpay is available on window
                    if (window.Razorpay) {
                        console.log("RZP Loaded successfully");
                        resolve(window.Razorpay);
                    } else {
                        reject(new Error("Razorpay not initialized"));
                    }
                };

                script.onerror = (error) => {
                    reject(new Error(`Failed to load Razorpay script: ${error}`));
                };

                document.body.appendChild(script);

                // Timeout safety
                setTimeout(() => {
                    if (!isScriptLoaded) {
                        reject(new Error("Script load timeout"));
                    }
                }, 10000);
            });
        };

        loadRazorpay()
            .then(() => {
                console.log("Razorpay ready to use");
            })
            .catch((error) => {
                console.error("Razorpay initialization failed:", error);
            });

        return () => {
            const script = document.querySelector('script[src*="checkout.razorpay.com"]');
            if (script) {
                document.body.removeChild(script);
            }
        };
    }, []);

    const makePayment = () => {
        if (paymentMode === PaymentMethod.NEFT) {
            setshowNEFTUI(true);
        } else {
            if (!paymentMode) {
                return false;
            }

            const payload = {
                paymentType: paymentMode,
            };

            postOrderDetailsByISIN({
                isin: state.isin ?? "",
                orderId: state.orderId ?? "",
                payload,
            }).then((res) => {
                if (!res || !res.success) {
                    return;
                }

                if (res && res.success && res.data) {
                    const order = {
                        url: state?.postDigioTxnData?.pgDetails?.callback,
                        name: state.postDigioTxnData?.name,
                        email: state.postDigioTxnData?.email,
                        phone: thirdPartyData?.phone,
                        key: state.postDigioTxnData?.key_id,
                        amount: state.postDigioTxnData?.rzpOrderDetails?.amount,
                        orderId: state.postDigioTxnData?.orderId,
                        currency: state.postDigioTxnData?.rzpOrderDetails?.currency,
                        units: state.postDigioTxnData?.units,
                        userAmount: state.postDigioTxnData?.userAmount,
                        contact: thirdPartyData?.phone,
                    };
                    goToRazorPay(order);
                }
            });
        }
    };

    const createRazorpayInstance = (order: OrderDetails) => {
        // localStorage.setItem("orderid", order.orderId);

        const options = {
            name: "Alpha Fintech",
            amount: order.amount,
            order_id: order.orderId,
            currency: order.currency,
            theme: {
                color: "#283A97",
            },
            personalization: "false",
            modal: {
                ondismiss: function () {
                    if (isAdvisoryFlow) {
                        window.location.replace(
                            `${process.env.ADVISORY_URL}/orders/${state.orderId}/payments/status?status=failure`,
                        );
                    } else {
                        const payload = {
                            "error[code]": "BAD_REQUEST_ERROR",
                            "error[description]": "Your payment didn't go through",
                            "error[source]": "bank",
                            "error[step]": "payment_authorization",
                            "error[reason]": "payment_failed",
                            "error[metadata]": { payment_id: null, order_id: state.orderId ?? "" },
                            ISIN: state.isin,
                            // "userId": "<USERID>"
                        };

                        RZPModalDismiss(state.isin ?? "", {
                            ...payload,
                            ISIN: state.isin ?? "",
                        }).then(() => {
                            if (isAdvisoryFlow) {
                                window.location.replace(
                                    `${process.env.ADVISORY_URL}/orders/${state.orderId}/payments/status?status=failure`,
                                );
                            }
                            dispatch({
                                type: ACTIONS.SET_FAILED,
                                payload: { error: "Payment failed" },
                            });
                        });
                        // statusFailedRazorpay(null, productDetails.ISIN, orderId, payload);
                        // localStorage.removeItem("orderid");
                        // setIsOngoingOrder(false);

                        // setTimeout(() => {
                        //     window.location.reload();
                        // }, 1000);
                    }
                },
            },
            prefill: {
                name: order.name,
                email: order.email,
                contact: order.phone,
            },
            redirect: false,
            key: order.key,
            // callback_url: order.url,
            description: "Alpha Fintech",
            image: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxOCAxOCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0wLjcyNzI5NSAwLjcyNzI2NEgxNy4yNzI4VjE3LjI3MjdIMC43MjcyOTVWMC43MjcyNjRaTTIuOTMzMzkgMi45MzMyOEgxNS4wNjY3VjQuMzI5NTVDMTAuNTEwNiA0Ljk0Mjk3IDcuNDc3ODYgOC44ODI2NCA2LjkzMTg0IDEwLjkzMDJDNS43NDk4NiA4Ljk2MDI4IDMuOTAwNzEgOC4yODI5NSAyLjkzMzM5IDcuOTQ1MDRWMi45MzMyOFpNMi45MzMzOSAxMC42NjIzVjE1LjA2NjZINS45NjYxM0M1LjkxODczIDEzLjgwOTYgNS4yNjYzMyAxMS41OTMgMi45MzMzOSAxMC42NjIzWk04LjQ1NzQ2IDE1LjA2NjZIMTUuMDY2N1Y2LjY5MjI0QzEyLjg1NjkgNy4zMDA4NSA4LjY5OTQ5IDkuNzQ3NDMgOC40NTc0NiAxNS4wNjY2WiIgZmlsbD0iIzI4M0E5NyIvPgo8L3N2Zz4K",
            handler: function (response: RazorpayPaymentDetails) {
                if (typeof response?.error === "object" && Object.entries(response.error).length) {
                    if (isAdvisoryFlow) {
                        RZPCallback(state.isin ?? "", response).then(() => {
                            window.location.replace(
                                `${process.env.ADVISORY_URL}/orders/${response?.error?.metadata?.payment_id}/payments/status?status=failure`,
                            );
                        });
                    } else {
                        RZPCallback(state.isin ?? "", response).then((res) => {
                            if (!res.success) {
                                dispatch({
                                    type: ACTIONS.SET_FAILED,
                                    payload: { error: "Payment failed" },
                                });
                            }
                        });
                    }
                } else {
                    if (isAdvisoryFlow) {
                        RZPCallback(state.isin ?? "", response).then(() => {
                            window.location.replace(
                                `${process.env.ADVISORY_URL}/orders/${response?.razorpay_order_id}/payments/status?status=success`,
                            );
                        });
                        // advisorySubmitPaymentSuccessRazorpay(
                        //     null,
                        //     productDetails.ISIN,
                        //     response,
                        // ).then(() => {
                        //     window.location.replace(
                        //         `${process.env.REACT_APP_ADVISORY_URL}/orders/${response?.razorpay_order_id}/payments/status?status=success`,
                        //     );
                        // });
                    } else {
                        RZPCallback(state.isin ?? "", response).then((res) => {
                            if (res.success) {
                                appendUUIDToURL();
                                dispatch({ type: ACTIONS.SET_COMPLETED });
                                setAnalyticsEvent("", "KYC_High Intent_Complete Purchase");
                                cleverTapTrackEvent("KYC_High Intent_Complete Purchase", {});
                                // initBranch()
                                //     .then(() => {
                                //         trackEvent(
                                //             "KYC_High Intent_Complete Purchase",
                                //             thirdPartyData?.email || "",
                                //             thirdPartyData?.phone || "",
                                //             userId || "",
                                //         );
                                //     })
                                //     .catch((error) =>
                                //         console.error("Error initializing Branch:", error),
                                //     );
                            }
                        });
                        // setPaymentStatus("success");
                        // setModalType("payment");
                        // setStatusMessage("Payment Successful");
                        // setSuccessModal(true);
                        // window.location.reload();
                    }
                }
            },
        };
        setisDialogOpen(false);
        // cleverTapTrackEvent(`US_Payment_Initiated_${product}`, {});
        // setAnalyticsEvent(
        //     window.location.pathname + window.location.hash,
        //     "investment_payment_initiated",
        // );
        // initBranch()
        //     .then(() => {
        //         trackEvent(`US_Payment_Initiated_${product}`);
        //     })
        //     .catch((error) => console.error("Error initializing Branch:", error));
        return new window.Razorpay(options);
    };

    const goToRazorPay = (order: OrderDetails) => {
        // setSuccessModal(false);
        // setSignatureStatus("");
        // if (isAdvisory) {
        //     history.replace(`/incredmoney-asset/${productType}/${product}?partner=icmAdvisory`);
        // } else {
        //     history.replace(`/incredmoney-asset/${productType}/${product}`);
        // }

        const razorPay = createRazorpayInstance(order);

        if (razorPay) {
            razorPay.open();

            razorPay.on(
                "payment.success",
                (response: RazorpayPaymentDetails) => {
                    if (isAdvisoryFlow) {
                        RZPCallback(state.isin ?? "", response).then(() => {
                            window.location.replace(
                                `${process.env.ADVISORY_URL}/orders/${response?.razorpay_order_id}/payments/status?status=success`,
                            );
                        });
                        // advisorySubmitPaymentSuccessRazorpay(
                        //     null,
                        //     productDetails.ISIN,
                        //     response,
                        // ).then(() => {
                        //     window.location.replace(
                        //         `${process.env.REACT_APP_ADVISORY_URL}/orders/${response?.razorpay_order_id}/payments/status?status=success`,
                        //     );
                        // });
                    } else {
                        RZPCallback(state.isin ?? "", response).then((res) => {
                            if (res.success) {
                                dispatch({ type: ACTIONS.SET_COMPLETED });
                            }
                        });
                    }
                },
                (err: RazorpayPaymentDetails) => {
                    console.log("Payment Success (Error):", err);
                },
            );

            razorPay.on(
                "payment.failed",
                (response: RazorpayPaymentDetails) => {
                    if (isAdvisoryFlow) {
                        RZPCallback(state.isin ?? "", response).then(() => {
                            window.location.replace(
                                `${process.env.ADVISORY_URL}/orders/${response?.error?.metadata?.payment_id}/payments/status?status=failure`,
                            );
                        });

                        // advisorySubmitPaymentFailedRazorpay(
                        //     null,
                        //     productDetails.ISIN,
                        //     response,
                        // ).then(() => {
                        //     window.location.replace(
                        //         `${process.env.REACT_APP_ADVISORY_URL}/orders/${response?.error?.metadata?.payment_id}/payments/status?status=failure`,
                        //     );
                        // });
                    } else {
                        // Can add a .then if any other operation to be done in future
                        RZPCallback(state.isin ?? "", response);
                    }
                },
                (err: RazorpayPaymentDetails) => {
                    if (isAdvisoryFlow) {
                        RZPCallback(state.isin ?? "", err).then(() => {
                            window.location.replace(
                                `${process.env.ADVISORY_URL}/orders/${err?.error?.metadata?.payment_id}/payments/status?status=failure`,
                            );
                        });
                        // advisorySubmitPaymentFailedRazorpay(null, productDetails.ISIN, err).then(
                        //     () => {
                        //         window.location.replace(
                        //             `${process.env.REACT_APP_ADVISORY_URL}/orders/1234/payments/status?status=failure`,
                        //         );
                        //     },
                        // );
                    } else {
                        RZPCallback(state.isin ?? "", err).then(() => {
                            dispatch({
                                type: ACTIONS.SET_ERROR,
                                payload: { error: "Payment failed" },
                            });
                        });
                    }
                },
            );
        }
    };

    const MaskedNumber = ({ number }: { number?: string }) => {
        const maskNumber = (num?: string) => {
            if (!num) return ""; // Handle undefined or empty values
            return "X".repeat(num.length - 4) + num.slice(-4);
        };

        return <>{`Account Number - ${maskNumber(number)}`}</>;
    };

    return showNEFTUI ? (
        <NEFTModal isAdvisoryFlow={isAdvisoryFlow} />
    ) : (
        <Dialog open={isDialogOpen}>
            <DialogContent className="md:scale-[0.8] overflow-auto w-[90%] !rounded-3xl px-s12 pb-s12 pt-s14 md:min-h-[596px] md:min-w-[556px] md:gap-s11 [&>button:last-child]:hidden">
                <VisuallyHidden.Root>
                    <DialogTitle>Select Payment Method</DialogTitle>
                </VisuallyHidden.Root>
                <div className="w-full">
                    <div className="w-full">
                        <div className="flex flex-col justify-center">
                            <Image
                                src={`https://assets.incredmoney.com/images/webp/incred-money.webp`}
                                alt="InCred Money"
                                height={100}
                                width={100}
                                priority
                                className="mb-5 h-[32px] w-[90px] cursor-pointer sm:h-[42px] sm:w-[119px]"
                            />
                            <p className="mb-s11 text-left text-lg font-semibold md:text-3xl">
                                Select Payment Mode
                            </p>
                        </div>
                    </div>
                    <div>
                        <div className="mb-s11 flex flex-col flex-wrap gap-6">
                            {paymentMethodConfigs.map(
                                (config) =>
                                    config.condition && (
                                        <div key={config.key} className="">
                                            <label
                                                className="flex cursor-pointer items-start space-x-3"
                                                onClick={() => setpaymentMode(config.method)}
                                            >
                                                <label className="relative flex cursor-pointer items-center">
                                                    <input
                                                        name="paymentMethod"
                                                        type="radio"
                                                        value={config.key}
                                                        checked={paymentMode === config.method}
                                                        onChange={() =>
                                                            setpaymentMode(config.method)
                                                        }
                                                        className="peer h-5 w-5 cursor-pointer appearance-none rounded-full border border-slate-300 transition-all checked:border-brand-primary-500"
                                                        id="red-600"
                                                    />
                                                    <span className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-brand-primary-500 opacity-0 transition-opacity duration-200 peer-checked:opacity-100"></span>
                                                </label>
                                                <div className="flex flex-col">
                                                    <span
                                                        className={`text-sm font-semibold leading-[20.8px] tracking-[-0.14px] md:text-md ${paymentMode === config.method ? "text-brand-primary-500" : "text-neutral-900"}`}
                                                    >
                                                        {config.label}
                                                    </span>
                                                    {config.text &&
                                                    paymentMode === config.method ? (
                                                        <span
                                                            className={`pt-2 text-sm leading-[20.8px] tracking-[-0.14px] text-neutral-400`}
                                                        >
                                                            {config.text}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </label>
                                        </div>
                                    ),
                            )}
                        </div>

                        {/* <div className="mt-4 rounded-xl border-2 border-neutral-100 p-4">
                            <p className="text-xs leading-[19.2px] tracking-[0.12px] text-neutral-300">
                                Net Banking facility of Citibank is currently not available. We are
                                working with our partner bank to enable it soon.
                            </p>
                            <br />
                            <p className="text-xs leading-[19.2px] tracking-[0.12px] text-neutral-300">
                                If you wish to pay through any of the above banks, please select UPI
                                or NEFT/RTGS mode to make the payment.
                            </p>
                        </div> */}

                        <div className="my-10 flex w-full flex-col gap-3">
                            <div className="flex w-full gap-3">
                                <Info />
                                <p className="tet-md font-semibold leading-[130%] md:text-lg">
                                    Important details regarding payment
                                </p>
                            </div>
                            <p className="text-sm font-regular leading-[160%] tracking-[1%] text-neutral-300">
                                Please make sure payment is made through account which is registered
                                with us during KYC completion
                            </p>
                        </div>

                        <div className="mt-10 flex w-full flex-col items-start justify-start">
                            <p className="mb-2 text-md font-semibold leading-[130%] text-neutral-500">
                                Bank details registered with us
                            </p>
                            <div className="w-full rounded-[12px] border border-[#0E0F171A] p-5 text-lg font-regular leading-[140%] text-neutral-300">
                                <MaskedNumber number={thirdPartyData?.accountNumber} />
                                <br />
                                {bankName && `Bank Name - ${bankName}`}
                            </div>
                        </div>
                    </div>
                </div>
                <RegularButton
                    disabled={!paymentMode}
                    size={isMobile ? ButtonSize.Medium : ButtonSize.Large}
                    className="max-w-full text-lg md:h-16"
                    label="Pay Now"
                    noIcon={true}
                    showRightIcon={false}
                    onClick={makePayment}
                />
            </DialogContent>
        </Dialog>
    );
};

export default PaymentStep;
